// src/pages/Licenses.tsx
import React, { useState, useEffect, useMemo } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import SelectCustomerModal from '@/components/SelectCustomerModal';
import LicenseBoardingPassCardRTL from '@/components/LicenseBoardingPassCardRTL';
import {
    Loader2,
    Award,
    Search as SearchIcon,
    Phone,
    Clipboard,
    Filter as FilterIcon,
    ChevronRight,
    ChevronLeft,
    UserPlus,
    Sparkles,
    Eye,
} from 'lucide-react';
import { licensesAPI } from '@/services/api';
import { toast } from 'sonner';

/* ---------- Types ---------- */
type License = {
    id: number;
    license_key: string;
    reference_code?: number;               // ✅ اضافه شد
    product_id: number;
    product?: { title?: string; agent_commission_pct?: number; price_annual?: string };
    product_title?: string;
    product_price?: number;
    authorized_price?: number;
    agent_commission_pct?: number;
    status?: string;
    issued_at?: string;
    expires_at?: string;

    agent_id?: number;
    invoice_id?: number;
    metadata?: any;
    created_at?: string;
    updated_at?: string;

    buyer_id?: number | null;
    buyer?: {
        id: number | string;
        first_name?: string;
        last_name?: string;
        phone_number?: string;
        email?: string;
        country_id?: string;
        province_id?: string;
        city_id?: string;
        address?: string;
        complexname?: string;
        complexnumber?: string;
    } | null;
};

/* ---------- Helpers ---------- */
const fa = new Intl.NumberFormat('fa-IR');
const faDate = (d?: string | number | Date | null) =>
    d ? new Date(d).toLocaleDateString('fa-IR') : '—';
const fullName = (b?: License['buyer']) =>
    ((b?.first_name || '') + ' ' + (b?.last_name || '')).trim() || '—';
const unitCommission = (l: any) => {
    const price = Number(l.authorized_price ?? 0);
    const pct = Number(l.agent_commission_pct ?? l.product?.agent_commission_pct ?? 0);
    return Math.round(price * (pct / 100));
};
const humanAssign = (l: any) => (l.buyer_id ? 'اختصاص‌داده‌شده' : 'آزاد');
const humanActive = (l: any) => {
    const s = String(l.status || '').toLowerCase();
    if (s === 'active') return 'فعال';
    if (s === 'expired') return 'منقضی';
    return l.status || '—';
};
const assignVariant = (l: any) => (l.buyer_id ? 'secondary' : 'default');
const activeVariant = (l: any) =>
    String(l.status || '').toLowerCase() === 'active' ? 'default' : 'outline';

// تب‌های آکاردئون
const SECTION_KEYS = {
    BUYER: 'buyer',
    FINANCIAL: 'financial',
    TIMELINE: 'timeline',
    META: 'meta',
} as const;
type SectionKey = (typeof SECTION_KEYS)[keyof typeof SECTION_KEYS];

const Licenses: React.FC = () => {
    const [licenses, setLicenses] = useState<License[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    // دیالوگ بررسی خریدار با موبایل
    const [checkDialogOpen, setCheckDialogOpen] = useState(false);
    const [checkPhone, setCheckPhone] = useState('');
    const [checkLoading, setCheckLoading] = useState(false);
    const [buyerInfo, setBuyerInfo] = useState<any>(null);

    // مودال انتخاب مشتری
    const [selectModalOpen, setSelectModalOpen] = useState(false);
    const [selectedLicense, setSelectedLicense] = useState<License | null>(null);

    // مودال جزئیات لایسنس
    const [detailsOpen, setDetailsOpen] = useState(false);
    const [detailsLicense, setDetailsLicense] = useState<License | null>(null);

    // فیلترها
    const [filtersOpen, setFiltersOpen] = useState(false);
    const [q, setQ] = useState('');
    const [statusAssign, setStatusAssign] = useState<'all' | 'free' | 'assigned'>('all');
    const [statusActive, setStatusActive] = useState<'all' | 'active' | 'inactive'>('all');

    // صفحه‌بندی
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);

    // وضعیت آکاردئون هر لایسنس (برای دسکتاپ)
    const [openSections, setOpenSections] = useState<Record<string, SectionKey | null>>({});

    useEffect(() => {
        fetchLicenses();
    }, []);

    const fetchLicenses = async () => {
        try {
            const data = await licensesAPI.getLicenses();
            setLicenses(Array.isArray(data) ? data : data?.licenses || []);
        } catch (err: any) {
            setError(err?.response?.data?.message || err?.message || 'خطا در دریافت لایسنس‌ها');
        } finally {
            setLoading(false);
        }
    };

    const toggleSection = (licenseId: string, key: SectionKey) => {
        setOpenSections((prev) => {
            const current = prev[licenseId];
            return {
                ...prev,
                [licenseId]: current === key ? null : key,
            };
        });
    };

    // بررسی خریدار با موبایل
    const handleCheckBuyer = async () => {
        if (!checkPhone) {
            toast.error('لطفاً شماره موبایل را وارد کنید');
            return;
        }
        setCheckLoading(true);
        try {
            const res = await licensesAPI.checkBuyerByPhone(checkPhone);
            setBuyerInfo(res);
            toast.success('اطلاعات خریدار دریافت شد');
        } catch (err: any) {
            toast.error(err?.response?.data?.message || err?.message || 'خریدار یافت نشد');
            setBuyerInfo(null);
        } finally {
            setCheckLoading(false);
        }
    };

    // خلاصه
    const totals = useMemo(() => {
        const count = licenses.length;
        const free = licenses.filter((l: any) => !l.buyer_id).length;
        const assigned = count - free;
        return { count, free, assigned };
    }, [licenses]);

    // فیلتر (جستجو روی محصول + کلید + reference_code + buyer)
    const filtered = useMemo(() => {
        const text = q.trim().toLowerCase();
        return licenses.filter((l: License) => {
            const productTitle = l.product_title || l.product?.title || '';
            const b = l.buyer || null;
            const ref = l.reference_code ? String(l.reference_code) : '';

            const textMatch =
                !text ||
                [
                    l.license_key,
                    ref,                                       // ✅ اضافه شد
                    productTitle,
                    String(l.product_id ?? ''),
                    String(l.buyer_id ?? ''),
                    b?.first_name,
                    b?.last_name,
                    b?.phone_number,
                    b?.email,
                ]
                    .filter(Boolean)
                    .some((t) => String(t).toLowerCase().includes(text));

            const assignMatch =
                statusAssign === 'all' ||
                (statusAssign === 'free' && !l.buyer_id) ||
                (statusAssign === 'assigned' && !!l.buyer_id);

            const s = String(l.status || '').toLowerCase();
            const activeMatch =
                statusActive === 'all' ||
                (statusActive === 'active' && s === 'active') ||
                (statusActive === 'inactive' && s !== 'active');

            return textMatch && assignMatch && activeMatch;
        });
    }, [licenses, q, statusAssign, statusActive]);

    // صفحه‌بندی
    const totalItems = filtered.length;
    const totalPages = Math.max(1, Math.ceil(totalItems / pageSize));

    useEffect(() => {
        if (page > totalPages) setPage(totalPages);
    }, [totalPages, page]);

    const pageSlice = useMemo(() => {
        const start = (page - 1) * pageSize;
        return filtered.slice(start, start + pageSize);
    }, [filtered, page, pageSize]);

    const openDetails = (license: License) => {
        setDetailsLicense(license);
        setDetailsOpen(true);
    };

    /* ---------- رندر ---------- */
    if (loading) {
        return (
            <div
                className="flex items-center justify-center h-screen bg-gradient-to-br from-green-50 via-emerald-50 to-teal-50">
                <div className="text-center">
                    <Loader2 className="h-12 w-12 animate-spin text-green-600 mx-auto mb-4" />
                    <p className="text-green-700 font-medium">در حال بارگذاری...</p>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <Alert variant="destructive" dir="rtl" className="m-4">
                <AlertDescription>{error}</AlertDescription>
            </Alert>
        );
    }

    return (
        <div
            className="min-h-screen space-y-6 p-4 md:p-8 bg-gradient-to-br from-green-50 via-emerald-50 to-teal-50"
            dir="rtl"
        >
            {/* هدر اصلی */}
            <div
                className="relative overflow-hidden rounded-2xl bg-gradient-to-r from-green-600 via-emerald-600 to-teal-600 p-8 shadow-2xl">
                <div
                    className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZGVmcz48cGF0dGVybiBpZD0iZ3JpZCIgd2lkdGg9IjQwIiBoZWlnaHQ9IjQwIiBwYXR0ZXJuVW5pdHM9InVzZXJTcGFjZU9uVXNlIj48cGF0aCBkPSJNIDQwIDAgTCAwIDAgMCA0MCIgZmlsbD0ibm9uZSIgc3Ryb2tlPSJ3aGl0ZSIgc3Ryb2tlLW9wYWNpdHk9IjAuMSIgc3Ryb2tlLXdpZHRoPSIxIi8+PC9wYXR0ZXJuPjwvZGVmcz48cmVjdCB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiBmaWxsPSJ1cmwoI2dyaWQpIi8+PC9zdmc+')] opacity-20" />
                <div className="relative flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                    <div className="text-white space-y-2">
                        <div className="flex items-center gap-3">
                            <Sparkles className="h-8 w-8" />
                            <h1 className="text-3xl md:text-4xl font-bold">مدیریت لایسنس‌ها</h1>
                        </div>
                        <p className="text-green-100 text-lg">
                            مشاهده، فیلتر و اختصاص لایسنس‌های محصولات
                        </p>
                    </div>

                    <div className="flex flex-wrap gap-3">
                        <div
                            className="bg-white/20 w-full md:w-auto flex flex-row justify-between items-center md:block backdrop-blur-sm rounded-xl px-5 py-3 text-white border border-white/30">
                            <div className="text-sm opacity-90">کل لایسنس‌ها</div>
                            <div className="text-2xl font-bold">{fa.format(totals.count)}</div>
                        </div>
                        <div
                            className="bg-white/20 w-full md:w-auto flex flex-row justify-between items-center md:block backdrop-blur-sm rounded-xl px-5 py-3 text-white border border-white/30">
                            <div className="text-sm opacity-90">آزاد</div>
                            <div className="text-2xl font-bold text-blue-100">
                                {fa.format(totals.free)}
                            </div>
                        </div>
                        <div
                            className="bg-white/20 backdrop-blur-sm w-full md:w-auto flex flex-row justify-between items-center md:block rounded-xl px-5 py-3 text-white border border-white/30">
                            <div className="text-sm opacity-90">اختصاص‌داده‌شده</div>
                            <div className="text-2xl font-bold text-green-200">
                                {fa.format(totals.assigned)}
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* اکشن‌ها: بررسی خریدار */}
            <div className="flex flex-col md:flex-row md:justify-between gap-3">
                <div className="flex-1" />
                <div className="flex flex-wrap gap-2">
                    <Dialog open={checkDialogOpen} onOpenChange={setCheckDialogOpen}>
                        <Button
                            asChild
                            variant="outline"
                            className="border-green-300 text-green-700 hover:bg-green-50 bg-white/80 w-full md:w-auto"
                        >
                            <div onClick={() => setCheckDialogOpen(true)}>
                                <SearchIcon className="ml-2 h-4 w-4" />
                                بررسی خریدار با موبایل
                            </div>
                        </Button>
                        <DialogContent dir="rtl">
                            <DialogHeader>
                                <DialogTitle>بررسی خریدار با موبایل</DialogTitle>
                                <DialogDescription>
                                    شماره موبایل خریدار را وارد کنید
                                </DialogDescription>
                            </DialogHeader>
                            <div className="space-y-4">
                                <div className="space-y-2">
                                    <Label htmlFor="checkPhone">شماره موبایل</Label>
                                    <div className="relative">
                                        <Phone className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                                        <Input
                                            id="checkPhone"
                                            type="tel"
                                            placeholder="0912xxxxxxx"
                                            value={checkPhone}
                                            onChange={(e) => setCheckPhone(e.target.value)}
                                            className="pl-10"
                                            dir="ltr"
                                        />
                                    </div>
                                </div>

                                {buyerInfo && (
                                    <Alert className="border-green-200 bg-green-50/70">
                                        <AlertDescription>
                                            <strong>نتیجه:</strong>
                                            <br />
                                            {buyerInfo.buyer ? (
                                                <>
                                                    نام:{' '}
                                                    {(buyerInfo.buyer.first_name || '') +
                                                        ' ' +
                                                        (buyerInfo.buyer.last_name || '')}
                                                    <br />
                                                    موبایل:{' '}
                                                    {buyerInfo.buyer.phone_number || '—'}
                                                    <br />
                                                    ایمیل: {buyerInfo.buyer.email || '—'}
                                                </>
                                            ) : (
                                                <>
                                                    {buyerInfo.name || '—'}
                                                    <br />
                                                    ایمیل: {buyerInfo.email || '—'}
                                                    <br />
                                                    وضعیت:{' '}
                                                    {buyerInfo.status ||
                                                        (buyerInfo.exists ? 'موجود' : 'یافت نشد')}
                                                </>
                                            )}
                                        </AlertDescription>
                                    </Alert>
                                )}

                                <Button
                                    onClick={handleCheckBuyer}
                                    disabled={checkLoading}
                                    className="w-full bg-green-600 hover:bg-green-700 text-white"
                                >
                                    {checkLoading ? (
                                        <>
                                            <Loader2 className="ml-2 h-4 w-4 animate-spin" />
                                            در حال بررسی…
                                        </>
                                    ) : (
                                        'بررسی خریدار'
                                    )}
                                </Button>
                            </div>
                        </DialogContent>
                    </Dialog>
                </div>
            </div>

            {/* فیلترها */}
            <Card className="border-green-200/60 shadow-lg bg-white/80 backdrop-blur-sm">
                <CardHeader className="py-4 bg-gradient-to-r from-green-50 to-emerald-50">
                    <div className="flex items-center justify-between gap-2">
                        <div className="flex items-center gap-3">
                            <div className="h-9 w-9 rounded-lg bg-green-600 flex items-center justify-center">
                                <FilterIcon className="h-4 w-4 text-white" />
                            </div>
                            <div>
                                <CardTitle className="text-green-900 text-base">
                                    فیلترها و جستجو
                                </CardTitle>
                                <CardDescription className="text-green-700 text-sm">
                                    جستجو در کد یکتا، کلید، محصول و خریدار + وضعیت اختصاص و فعال بودن
                                </CardDescription>
                            </div>
                        </div>
                        <Button
                            variant="outline"
                            className="border-green-300 text-green-700 hover:bg-green-100"
                            onClick={() => setFiltersOpen((v) => !v)}
                        >
                            <FilterIcon className="ml-2 h-4 w-4" />
                            {filtersOpen ? 'بستن' : 'نمایش'}
                        </Button>
                    </div>
                </CardHeader>

                {filtersOpen && (
                    <CardContent className="space-y-4 pt-4">
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            <div className="space-y-2">
                                <Label className="text-green-900 font-medium">
                                    جستجو (کد یکتا/کلید/محصول/خریدار)
                                </Label>
                                <div className="relative">
                                    <SearchIcon className="absolute left-3 top-3 h-4 w-4 text-green-400" />
                                    <Input
                                        value={q}
                                        onChange={(e) => {
                                            setQ(e.target.value);
                                            setPage(1);
                                        }}
                                        placeholder="مثال: ۱۰۰۱ یا 5745… یا نام محصول یا نام/موبایل مشتری"
                                        className="pl-10 h-11 border-green-200 focus:ring-green-500 focus:border-green-500 rounded-lg"
                                    />
                                </div>
                            </div>

                            <div className="space-y-2">
                                <Label className="text-green-900 font-medium">
                                    وضعیت اختصاص
                                </Label>
                                <select
                                    value={statusAssign}
                                    onChange={(e) => {
                                        setStatusAssign(e.target.value as any);
                                        setPage(1);
                                    }}
                                    className="h-11 w-full rounded-lg border border-green-200 bg-white px-3 text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
                                >
                                    <option value="all">🔘 همه</option>
                                    <option value="free">🟢 آزاد</option>
                                    <option value="assigned">🧾 اختصاص‌داده‌شده</option>
                                </select>
                            </div>

                            <div className="space-y-2">
                                <Label className="text-green-900 font-medium">
                                    وضعیت فعال/غیرفعال
                                </Label>
                                <select
                                    value={statusActive}
                                    onChange={(e) => {
                                        setStatusActive(e.target.value as any);
                                        setPage(1);
                                    }}
                                    className="h-11 w-full rounded-lg border border-green-200 bg-white px-3 text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
                                >
                                    <option value="all">🔘 همه</option>
                                    <option value="active">✅ فعال</option>
                                    <option value="inactive">❌ غیرفعال/منقضی</option>
                                </select>
                            </div>
                        </div>

                        <div className="flex justify-end pt-1">
                            <Button
                                variant="outline"
                                className="border-green-300 text-green-700 hover:bg-green-50"
                                onClick={() => {
                                    setQ('');
                                    setStatusAssign('all');
                                    setStatusActive('all');
                                    setPage(1);
                                }}
                            >
                                پاک‌سازی فیلترها
                            </Button>
                        </div>
                    </CardContent>
                )}
            </Card>

            {/* لیست لایسنس‌ها */}
            {licenses.length === 0 ? (
                <Card className="border-green-200/60 bg-white/80 backdrop-blur-sm shadow-lg">
                    <CardContent className="text-center py-16">
                        <Award className="h-16 w-16 mx-auto mb-4 text-green-200" />
                        <h3 className="text-xl font-semibold text-gray-900 mb-2">
                            لایسنس یافت نشد
                        </h3>
                        <p className="text-gray-500">
                            به‌محض صدور یا خرید، لایسنس‌ها اینجا نمایش داده می‌شوند
                        </p>
                    </CardContent>
                </Card>
            ) : (
                <Card className="border-green-200/60 bg-white/85 backdrop-blur-sm shadow-lg">
                    <CardHeader className="text-right pb-3">
                        <CardTitle className="text-green-900">مدیریت لایسنس</CardTitle>
                        <CardDescription className="text-green-700">
                            {fa.format(totalItems)} مورد مطابق فیلترها یافت شد
                        </CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-4">
                        {/* موبایل: کارت‌های ساده */}
                        <div className="grid gap-3 sm:hidden">
                            {pageSlice.length === 0 ? (
                                <div className="text-center py-10 text-green-700 text-sm">
                                    موردی یافت نشد
                                </div>
                            ) : (
                                pageSlice.map((license: License) => {
                                    const price = Number(license.authorized_price ?? license.product_price ?? 0);
                                    const pct = Number(license.agent_commission_pct ?? 0);
                                    const perUnitCommission = unitCommission(license);
                                    const productTitle =
                                        license.product_title || license.product?.title || `محصول ${license.product_id}`;
                                    const canAssign = !license.buyer_id;
                                    const displayCode = license.reference_code ?? license.license_key; // ✅ استفاده از reference_code

                                    return (
                                        <div
                                            key={license.id}
                                            className="rounded-2xl border border-emerald-600/60 bg-gradient-to-br from-emerald-50 via-green-50 to-white p-3.5 shadow-sm space-y-3"
                                        >
                                            {/* Top: کد یکتا & status */}
                                            <div className="flex items-start justify-between gap-3">
                                                <div className="space-y-1 min-w-0 flex-1">
                                                    <div className="text-[11px] text-emerald-700">کد یکتا</div> {/* تغییر برچسب */}
                                                    <div
                                                        className="font-mono text-[11px] sm:text-xs truncate px-2 py-1 rounded-lg bg-white/60 border border-emerald-100"
                                                        dir="ltr"
                                                    >
                                                        {displayCode}
                                                    </div>
                                                </div>

                                                <div className="flex flex-col items-end gap-1 ml-1 shrink-0">
                                                    <Badge variant={assignVariant(license)}
                                                           className="text-[10px] px-2 py-1">
                                                        {humanAssign(license)}
                                                    </Badge>
                                                    <Badge variant={activeVariant(license)}
                                                           className="text-[10px] px-2 py-1">
                                                        {humanActive(license)}
                                                    </Badge>
                                                </div>
                                            </div>

                                            {/* Middle: info list (بدون تغییر) */}
                                            <div className="text-[12px] leading-5 text-emerald-900 space-y-1.5">
                                                <div className="truncate font-medium text-emerald-900">
                                                    محصول: <span className="font-normal">{productTitle}</span>
                                                </div>
                                                <div className="flex items-center justify-between">
                                                    <span className="text-emerald-800">قیمت مجاز</span>
                                                    <span className="font-medium">{fa.format(price / 10)} تومان</span>
                                                </div>
                                                <div className="flex items-center justify-between">
                                                    <span className="text-emerald-800">درصد کمیسیون</span>
                                                    <span className="font-medium">{fa.format(pct)}٪</span>
                                                </div>
                                                <div className="flex items-center justify-between">
                                                    <span className="text-emerald-800">کمیسیون هر واحد</span>
                                                    <span
                                                        className="font-medium">{fa.format(perUnitCommission / 10)} تومان</span>
                                                </div>

                                                <div
                                                    className="pt-1 border-t border-emerald-100 mt-1.5 space-y-0.5 text-[11px] text-emerald-800">
                                                    <div className={'flex flex-row justify-between items-center'}>
                                                        {license.buyer ? (
                                                            <>
                                                                <span title={fullName(license.buyer) || ""}>
                                                                    خریدار: {fullName(license.buyer).slice(0, 20)}
                                                                </span>
                                                                <span dir="ltr" className="font-mono">
                                                                    📞 {license.buyer.phone_number || "—"}
                                                                </span>
                                                            </>
                                                        ) : (
                                                            "—"
                                                        )}
                                                    </div>
                                                    <div className="flex flex-wrap gap-x-1 justify-between gap-y-0.5">
                                                        <span>صدور: {faDate(license.issued_at)}</span>
                                                        <span className="opacity-60">|</span>
                                                        <span>انقضا: {faDate(license.expires_at)}</span>
                                                    </div>
                                                </div>
                                            </div>

                                            {/* Bottom: actions */}
                                            <div
                                                className="flex flex-wrap items-center gap-2 pt-1 border-t border-emerald-100 mt-1">
                                                <Button
                                                    variant="outline"
                                                    size="sm"
                                                    className="h-8 px-2 border-emerald-200 bg-white/60 text-emerald-700 hover:bg-emerald-50"
                                                    onClick={() => {
                                                        const code = license.reference_code ?? license.license_key;
                                                        navigator.clipboard.writeText(String(code))
                                                            .then(() => toast.success("کد یکتا کپی شد"));
                                                    }}
                                                >
                                                    <Clipboard className="h-3.5 w-3.5 ml-1" />
                                                    <span className="text-[11px]">کپی</span>
                                                </Button>

                                                <Button
                                                    variant="outline"
                                                    size="sm"
                                                    className="h-8 px-3 border-emerald-200 bg-white/60 text-emerald-800 hover:bg-emerald-50"
                                                    onClick={() => openDetails(license)}
                                                >
                                                    <Eye className="ml-1.5 h-4 w-4" />
                                                    <span className="text-[11px]">جزئیات</span>
                                                </Button>

                                                {canAssign && (
                                                    <Button
                                                        variant="outline"
                                                        size="sm"
                                                        className="h-8 px-3 border-emerald-200 bg-emerald-600/5 text-emerald-800 hover:bg-emerald-50"
                                                        onClick={() => {
                                                            setSelectedLicense(license);
                                                            setSelectModalOpen(true);
                                                        }}
                                                    >
                                                        <UserPlus className="ml-1.5 h-4 w-4" />
                                                        <span className="text-[11px]">اختصاص</span>
                                                    </Button>
                                                )}
                                            </div>
                                        </div>
                                    );
                                })
                            )}
                        </div>

                        {/* دسکتاپ: کارت پرواز + آکاردئون (تغییر نکرده) */}
                        <div className="hidden sm:block">
                            {pageSlice.length === 0 ? (
                                <div className="text-center py-8 text-green-700">موردی یافت نشد</div>
                            ) : (
                                <div className="space-y-4">
                                    {pageSlice.map((license) => (
                                        <LicenseBoardingPassCardRTL
                                            key={license.id}
                                            license={license}
                                            onAssignBuyer={(lic) => {
                                                setSelectedLicense(license);
                                                setSelectModalOpen(true);
                                            }}
                                            onOpenDetails={(lic) => {
                                                console.log('open details for', lic.id);
                                                openDetails(license);
                                            }}
                                        />
                                    ))}
                                </div>
                            )}
                        </div>

                        {/* کنترل صفحه‌بندی */}
                        <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3">
                            <div className="flex items-center gap-2">
                                <span className="text-sm text-gray-600">
                                    آیتم در هر صفحه:
                                </span>
                                <select
                                    value={pageSize}
                                    onChange={(e) => {
                                        setPageSize(parseInt(e.target.value, 10));
                                        setPage(1);
                                    }}
                                    className="h-9 rounded-md border border-green-300 bg-white px-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-600"
                                >
                                    <option value={5}>۵</option>
                                    <option value={10}>۱۰</option>
                                    <option value={20}>۲۰</option>
                                    <option value={50}>۵۰</option>
                                </select>
                            </div>

                            <div className="text-sm text-gray-700 text-center bg-green-50 px-4 py-2 rounded-lg">
                                صفحه{' '}
                                <span className="text-green-700 font-bold">
                                    {fa.format(page)}
                                </span>{' '}
                                از{' '}
                                <span className="text-green-700 font-bold">
                                    {fa.format(totalPages)}
                                </span>{' '}
                                — مجموع{' '}
                                <span className="text-green-700 font-bold">
                                    {fa.format(totalItems)}
                                </span>{' '}
                                لایسنس
                            </div>

                            <div className="flex w-full sm:w-auto items-center gap-2">
                                <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                                    disabled={page <= 1}
                                    className="flex-1 sm:flex-none border-green-400 text-green-700 hover:bg-green-50 disabled:opacity-50"
                                >
                                    <ChevronRight className="ml-1 h-4 w-4" />
                                    قبلی
                                </Button>
                                <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() =>
                                        setPage((p) => Math.min(totalPages, p + 1))
                                    }
                                    disabled={page >= totalPages}
                                    className="flex-1 sm:flex-none border-green-400 text-green-700 hover:bg-green-50 disabled:opacity-50"
                                >
                                    بعدی
                                    <ChevronLeft className="mr-1 h-4 w-4" />
                                </Button>
                            </div>
                        </div>
                    </CardContent>
                </Card>
            )}

            {/* مودال جزئیات لایسنس (پاپ‌آپ جزئیات بیشتر) */}
            <Dialog open={detailsOpen} onOpenChange={setDetailsOpen}>
                <DialogContent dir="rtl" className="sm:max-w-xl rounded-2xl">
                    <DialogHeader>
                        <DialogTitle className="flex items-center justify-between gap-2">
                            <span>جزئیات لایسنس</span>
                            {detailsLicense && (
                                <span
                                    className="font-mono text-xs bg-green-50 px-2 py-1 rounded-md"
                                    dir="ltr"
                                >
                                    {detailsLicense.reference_code ?? detailsLicense.license_key}
                                </span>
                            )}
                        </DialogTitle>
                        <DialogDescription>
                            اطلاعات کامل لایسنس، محصول و خریدار
                        </DialogDescription>
                    </DialogHeader>

                    {detailsLicense ? (
                        <div className="space-y-4 text-sm">
                            {/* محصول و قیمت‌ها */}
                            <div className="border rounded-lg p-3 bg-green-50/50 space-y-1">
                                <div className="font-semibold text-green-900 mb-1">
                                    محصول
                                </div>
                                <div className="text-gray-800">
                                    نام:{' '}
                                    {detailsLicense.product_title ||
                                        detailsLicense.product?.title ||
                                        `محصول ${detailsLicense.product_id}`}
                                </div>
                                <div className="text-gray-700">
                                    قیمت مجاز:{' '}
                                    {fa.format(
                                        (detailsLicense.authorized_price ?? 0) / 10,
                                    )}{' '}
                                    تومان
                                </div>
                                <div className="text-gray-700">
                                    درصد کمیسیون:{' '}
                                    {fa.format(
                                        detailsLicense.agent_commission_pct ??
                                        detailsLicense.product?.agent_commission_pct ??
                                        0,
                                    )}
                                    ٪
                                </div>
                                <div className="text-gray-700">
                                    کمیسیون هر واحد:{' '}
                                    {fa.format(
                                        unitCommission(detailsLicense) / 10,
                                    )}{' '}
                                    تومان
                                </div>
                            </div>

                            {/* وضعیت‌ها */}
                            <div className="border rounded-lg p-3 bg-white space-y-2">
                                <div className="font-semibold text-green-900 mb-1">
                                    وضعیت لایسنس
                                </div>
                                <div className="flex items-center justify-between">
                                    <span className="text-gray-700">وضعیت اختصاص:</span>
                                    <Badge variant={assignVariant(detailsLicense)}>
                                        {humanAssign(detailsLicense)}
                                    </Badge>
                                </div>
                                <div className="flex items-center justify-between">
                                    <span className="text-gray-700">وضعیت فعال:</span>
                                    <Badge variant={activeVariant(detailsLicense)}>
                                        {humanActive(detailsLicense)}
                                    </Badge>
                                </div>
                                <div className="flex items-center justify-between text-gray-700">
                                    <span>تاریخ صدور:</span>
                                    <span>{faDate(detailsLicense.issued_at)}</span>
                                </div>
                                <div className="flex items-center justify-between text-gray-700">
                                    <span>تاریخ انقضا:</span>
                                    <span>{faDate(detailsLicense.expires_at)}</span>
                                </div>
                            </div>

                            {/* خریدار */}
                            <div className="border rounded-lg p-3 bg-white space-y-2">
                                <div className="font-semibold text-green-900 mb-1">
                                    اطلاعات خریدار
                                </div>
                                {detailsLicense.buyer ? (
                                    <>
                                        <div className="flex items-center justify-between">
                                            <span className="text-gray-700">نام:</span>
                                            <span className="font-medium text-gray-900">
                                                {fullName(detailsLicense.buyer)}
                                            </span>
                                        </div>
                                        <div className="flex items-center justify-between">
                                            <span className="text-gray-700">موبایل:</span>
                                            <span dir="ltr">
                                                {detailsLicense.buyer.phone_number || '—'}
                                            </span>
                                        </div>
                                        <div className="flex items-center justify-between">
                                            <span className="text-gray-700">ایمیل:</span>
                                            <span
                                                className="truncate max-w-[210px] text-left"
                                                dir="ltr"
                                            >
                                                {detailsLicense.buyer.email || '—'}
                                            </span>
                                        </div>
                                        <div className="text-gray-700">
                                            آدرس:
                                            <span className="text-gray-800">
                                                {' '}
                                                {detailsLicense.buyer.address || '—'}
                                            </span>
                                        </div>
                                        <div className="text-gray-700 text-xs">
                                            مجتمع:{' '}
                                            {detailsLicense.buyer.complexname || '—'} / واحد:{' '}
                                            {detailsLicense.buyer.complexnumber || '—'}
                                        </div>
                                    </>
                                ) : (
                                    <p className="text-gray-500 text-sm">
                                        این لایسنس هنوز به خریدار اختصاص داده نشده است.
                                    </p>
                                )}
                            </div>
                        </div>
                    ) : (
                        <div className="py-6 text-center text-gray-500">
                            در حال بارگذاری جزئیات...
                        </div>
                    )}
                </DialogContent>
            </Dialog>

            {/* مودال انتخاب مشتری */}
            <SelectCustomerModal
                open={selectModalOpen}
                onClose={() => setSelectModalOpen(false)}
                onSelect={async (customer) => {
                    try {
                        if (!selectedLicense) return;
                        await licensesAPI.assignLicense({
                            licenseId: Number(selectedLicense.id),
                            firstName: customer.first_name,
                            lastName: customer.last_name,
                            phone: customer.phone_number,
                            email: customer.email,
                            country: customer.country_id || 'IR',
                            province: customer.province_id,
                            city: customer.city_id,
                            address: customer.address,
                            complexName: customer.complexname,
                            complexNumber: customer.complexnumber,
                        });
                        toast.success(
                            `لایسنس به ${customer.first_name} ${customer.last_name} اختصاص یافت`,
                        );
                        setSelectModalOpen(false);
                        await fetchLicenses();
                    } catch (err: any) {
                        toast.error(err?.message || 'خطا در اختصاص لایسنس');
                    }
                }}
            />
        </div>
    );
};

export default Licenses;
